// @givmo/board — Your settings → Calendar: the person's own calendar (CBG-CAL1 slice 1d; the approved
// frames `your-calendar-none`, `-connected`, `-attention`, and CBG-UX14's `ux14-your-settings`).
//
// ONE CONNECTION, THE PERSON'S OWN, FOR ANY GIVMO APP (CBG-UX14's Q3, 2026-11-01 15:14 PST): its words
// name no app, and its row names each app that uses it ("Board · 1 upcoming meeting: Nov 17"). On
// Board's own door the host leaves this card out: Board's "Calendar" card connects, reconnects and
// disconnects the same connection there (D-CBG-56).
//
// For a holder of `board_meeting:write`. Served by core's list (the officer sees their own personal
// connection), `POST …/oauth/start` to connect or reconnect, and `DELETE …/{id}` to disconnect —
// preceded by Board's choice for the meetings it sends (`DisconnectDialog`). No status word while it
// simply works: "Needs reconnecting" is the one that asks for an act, for ANY own calendar that isn't
// `connected` — Google stopped letting Board use it (`needs_reconnect`, the drawn frame), or its owner
// lost Board's scheduling string and core keeps it `organizer_left` even after they regain it. Such a
// calendar sends nothing until Reconnect brings the same account back; the lede claims no sending, so
// any calendar the person has reads the connected one.
//
// REVOKING NEVER WAITS ON BOARD (core's `org-integrations.md` §5). Where Board isn't on for the
// organization (`boardOn` false) the person's Board strings are masked, but core still lists their own
// calendar: the card then offers its owner Disconnect and nothing else — no lede, no Board read, and
// core's `DELETE` alone. Anyone else is listed nothing, and the card renders nothing.
//
// The composed Settings page has no module seam under "Your settings", so the host passes this card
// in its `yourSettingsLead`, after the account's "Sign-in & security" (the README's build note). It is
// mounted outside Board's pages, so the host hands it what Board's officer host would: the
// organization, the person's permission set, their backend id and address, the organization's name.
//
// DARK until served: core's list answers the route miss while `ORG_INTEGRATIONS_ENABLED` is off, and
// Board's senders read (which says whether Google is offered to connect) while both calendar flags
// are. The card renders only when core serves the list AND Board offers a calendar channel.

import { useState } from 'react';
import { Button, Card, CardHeader, Dialog } from '@givmo/design-system';
import { BoardOfficerProvider, hasBoardPermission } from '../../board-host';
import { CALENDAR_PROVIDER } from '../../calendar/calendar-connect';
import { meetingsSentFrom, useMeetingsById } from '../../calendar/calendar-model';
import {
  useCalendarSenders,
  useOrgConnections,
  type CalendarConnection,
} from '../../calendar/calendar-queries';
import { calendarWords as w } from '../../calendar/calendar-words';
import { useCalendarConnect } from '../../calendar/use-calendar-connect';
import { DisconnectDialog } from './account-dialogs';
import { AccountRow, CardTitle, meetingDay, organizationDomain } from './calendar-parts';
import { ConnectAnswer, ConnectDialog } from './connect-dialog';
import { CALENDAR_MANAGE_PERMISSION } from './board-calendar-card';

/** What the host hands the card (Board's officer host is not mounted on the Settings page). */
export interface YourCalendarCardProps {
  orgRef: string;
  /** The person's settled permission set off the console's standing read. */
  permissions: readonly string[];
  /** The person's backend user id (never a Firebase uid); null while unread. */
  userId: string | null;
  /** The person's own address; null while unread. */
  userEmail: string | null;
  /** The organization's name; null while unread. */
  orgName: string | null;
  /**
   * Board is on for the organization (the console's standing verdict). Off: a person without
   * `board_meeting:write` who owns a calendar is offered its Disconnect alone. Absent: on.
   */
  boardOn?: boolean;
}

/** The person's own live personal connection, if they have one. */
function ownConnection(
  rows: readonly CalendarConnection[],
  userId: string | null,
): CalendarConnection | undefined {
  if (userId === null) return undefined;
  return rows.find(
    (row) =>
      row.account_kind === 'personal' &&
      row.owner_user_id === userId &&
      row.status !== 'disconnected',
  );
}

export function YourCalendarCard(props: YourCalendarCardProps) {
  // The card's reads and dialogs speak Board's officer port and host, as Board's own pages do.
  return (
    <BoardOfficerProvider
      host={{
        orgRef: props.orgRef,
        isReady: true,
        isPending: false,
        isError: false,
        error: null,
        permissions: props.permissions,
        userId: props.userId,
        userEmail: props.userEmail,
        orgName: props.orgName,
      }}
    >
      <YourCalendar {...props} />
    </BoardOfficerProvider>
  );
}

type Open = 'connect' | 'disconnect' | null;

function YourCalendar({
  orgRef,
  permissions,
  userId,
  userEmail,
  orgName,
  boardOn = true,
}: YourCalendarCardProps) {
  const canWrite = hasBoardPermission(permissions, 'board_meeting:write');
  const canManage = hasBoardPermission(permissions, CALENDAR_MANAGE_PERMISSION);
  const revokeOnly = !canWrite && !boardOn;
  const connections = useOrgConnections(orgRef, canWrite || revokeOnly);
  const senders = useCalendarSenders(orgRef, canWrite);
  const listed = connections.data !== undefined && connections.data !== null;
  const served = listed && senders.data !== undefined && senders.data !== null;
  const meetings = useMeetingsById(orgRef, served);
  const flow = useCalendarConnect(orgRef);
  const [open, setOpen] = useState<Open>(null);

  if (revokeOnly) {
    const owned = listed ? ownConnection(connections.data ?? [], userId) : undefined;
    if (owned === undefined) return null;
    return (
      <Card testId="your-calendar">
        <CardHeader className="flex-col sm:flex-row">
          <CardTitle title={w.yourTitle} lede={null} />
        </CardHeader>
        <ul>
          <AccountRow
            testId="your-calendar-account"
            primary={owned.account_email}
            action={
              <Button
                appearance="saas"
                tone="secondary"
                size="sm"
                onClick={() => setOpen('disconnect')}
                testId="your-calendar-disconnect"
              >
                {w.disconnect}
              </Button>
            }
          />
        </ul>
        {/* Board can't say which meetings it sends: the plain confirmation, and core's DELETE alone. */}
        {open === 'disconnect' ? (
          <DisconnectDialog
            orgRef={orgRef}
            account={owned}
            own
            ownerName={null}
            meetings={null}
            senders={null}
            orgName={orgName ?? ''}
            onClose={() => setOpen(null)}
          />
        ) : null}
      </Card>
    );
  }

  // Not before the meetings list: a disconnect must know which meetings this calendar still sends.
  if (!canWrite || !served || !meetings.loaded) return null;
  const rows = connections.data ?? [];
  const mine = ownConnection(rows, userId);
  const canConnect = (senders.data?.offered_providers ?? []).includes(CALENDAR_PROVIDER);
  // Nothing to show and nothing to do: the card stays away rather than offer an act it can't take.
  if (mine === undefined && !canConnect) return null;
  const sent =
    mine === undefined ? [] : meetingsSentFrom(mine.account_email, meetings.byId.values());
  const boardAccountExists = rows.some(
    (row) => row.account_kind === 'board_account' && row.status !== 'disconnected',
  );
  const name = orgName ?? '';
  const domain = organizationDomain([userEmail, ...rows.map((row) => row.account_email)]);

  return (
    <Card testId="your-calendar">
      <CardHeader
        className="flex-col sm:flex-row"
        actions={
          mine === undefined ? (
            <Button
              appearance="saas"
              tone="secondary"
              size="sm"
              onClick={() => {
                flow.reset();
                setOpen('connect');
              }}
              testId="your-calendar-connect"
            >
              {w.connectYours}
            </Button>
          ) : undefined
        }
      >
        <CardTitle
          title={w.yourTitle}
          lede={mine !== undefined ? w.yourLedeConnected : w.yourLedeNone}
        />
      </CardHeader>

      {mine !== undefined ? (
        <ul>
          <AccountRow
            testId="your-calendar-account"
            primary={mine.account_email}
            status={mine.status !== 'connected' ? w.needsReconnecting : undefined}
            secondary={[w.usedByBoard, w.upcoming(sent.length, sent.map(meetingDay))]}
            note={mine.status === 'needs_reconnect' ? w.reconnectLine : undefined}
            action={
              <>
                <Button
                  appearance="saas"
                  tone="secondary"
                  size="sm"
                  onClick={() => setOpen('disconnect')}
                  testId="your-calendar-disconnect"
                >
                  {w.disconnect}
                </Button>
                {mine.status !== 'connected' && canConnect ? (
                  <Button
                    appearance="saas"
                    tone="primary"
                    size="sm"
                    loading={flow.starting}
                    onClick={() =>
                      flow.begin({
                        accountKind: 'personal',
                        reconnectConnectionId: mine.connection_id,
                        loginHint: mine.account_email,
                      })
                    }
                    testId="your-calendar-reconnect"
                  >
                    {w.reconnect}
                  </Button>
                ) : null}
              </>
            }
          />
        </ul>
      ) : null}

      {open === 'connect' ? (
        <ConnectDialog
          mode="mine"
          canManage={canManage}
          boardAccountExists={boardAccountExists}
          exampleDomain={domain}
          orgName={name}
          flow={flow}
          onClose={() => {
            flow.reset();
            setOpen(null);
          }}
        />
      ) : null}
      {open === null && flow.outcome !== null ? (
        <Dialog.Root open onOpenChange={(next) => !next && flow.reset()}>
          <Dialog.Content
            aria-label={w.connectTitle}
            width={448}
            className="flex flex-col gap-16"
            testId="calendar-connect-dialog"
          >
            <ConnectAnswer
              outcome={flow.outcome}
              boardAccount={false}
              onClose={flow.reset}
              onAgain={flow.reset}
            />
          </Dialog.Content>
        </Dialog.Root>
      ) : null}
      {open === 'disconnect' && mine !== undefined ? (
        <DisconnectDialog
          orgRef={orgRef}
          account={mine}
          own
          ownerName={null}
          meetings={sent}
          senders={senders.data ?? null}
          orgName={name}
          onClose={() => setOpen(null)}
        />
      ) : null}
    </Card>
  );
}
